import React, { useEffect, useRef, useState } from "react";
import { MapPin } from "lucide-react";
import { api } from "@/lib/api";
import { colors } from "@/lib/theme";

// Google Maps-powered location field. Falls back to a plain text input when
// GOOGLE_MAPS_API_KEY isn't configured (backend returns configured:false).
export function LocationField({ value, onChange, testID = "event-location-input" }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [coords, setCoords] = useState(null);
  const [sessionToken] = useState(() => Math.random().toString(36).slice(2));
  const skipNext = useRef(false);
  const debounce = useRef(null);

  // Local-first: quietly grab the user's location to bias suggestions nearby.
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => {},
      { enableHighAccuracy: false, timeout: 6000, maximumAge: 600000 }
    );
  }, []);

  useEffect(() => {
    if (skipNext.current) {
      skipNext.current = false;
      return;
    }
    // Only fetch/open once the user actually interacts with the field — never
    // auto-open the dropdown on mount (e.g. when editing an event).
    if (!focused) return;
    const text = (value || "").trim();
    if (text.length < 2) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    clearTimeout(debounce.current);
    debounce.current = setTimeout(async () => {
      try {
        const res = await api.placesAutocomplete(text, sessionToken, coords);
        if (res.configured && res.suggestions?.length) {
          setSuggestions(res.suggestions);
          setOpen(true);
        } else {
          setSuggestions([]);
          setOpen(false);
        }
      } catch {
        setSuggestions([]);
        setOpen(false);
      }
    }, 250);
    return () => clearTimeout(debounce.current);
  }, [value, focused, coords, sessionToken]);

  const pick = async (s) => {
    skipNext.current = true;
    onChange(s.description);
    setOpen(false);
    setSuggestions([]);
    try {
      await api.placeDetails(s.place_id, sessionToken);
    } catch {}
  };

  return (
    <div className="relative">
      <div className="flex items-center gap-2 rounded-xl px-3" style={{ background: "#2C2D3A", border: "1px solid #2C2D3A" }}>
        <MapPin size={16} color={colors.onSurfaceTertiary} />
        <input
          data-testid={testID}
          placeholder="Where's it happening?"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => { setFocused(true); if (suggestions.length) setOpen(true); }}
          onBlur={() => { setFocused(false); setTimeout(() => setOpen(false), 150); }}
          className="flex-1 bg-transparent py-3 font-display text-[15px] focus:outline-none"
          style={{ color: "#F8F9FA" }}
        />
      </div>
      {open && suggestions.length > 0 && (
        <div data-testid="location-suggestions" className="absolute z-10 left-0 right-0 mt-1 rounded-xl overflow-hidden" style={{ background: "#20212C", border: `1px solid ${colors.border}` }}>
          {suggestions.map((s) => (
            <button
              key={s.place_id}
              data-testid={`location-suggestion-${s.place_id}`}
              onMouseDown={(e) => { e.preventDefault(); pick(s); }}
              className="flex items-center gap-2 w-full text-left px-3 py-2.5 transition-colors hover:bg-[#2C2D3A]"
            >
              <MapPin size={14} color={colors.brandSecondary} />
              <span className="font-display text-[13.5px] truncate" style={{ color: colors.onSurfaceSecondary }}>{s.description}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
